import { useEffect, useState } from "react";
import { Download, Filter, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_ROWS = 3000;

/**
 * Reusable Excel export trigger with a Date-range + Vendor filter panel.
 *
 * Props:
 *   endpoint    — e.g. "/exports/purchase-invoices-excel"
 *   filename    — final downloaded filename (suggested, server may override)
 *   label       — Button label (default: "Export Excel")
 *   testid      — data-testid prefix (default: "excel-export")
 *   showVendor  — whether to render the Vendor filter (default true)
 */
export function ExportExcelButton({
  endpoint,
  filename = "export.xlsx",
  label = "Export Excel",
  testid = "excel-export",
  showVendor = true,
}) {
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [vendor, setVendor] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !showVendor) return;
    api.get("/vendors/picker").then(r => {
      const names = (r.data || []).map(v => v.name).filter(Boolean).sort();
      setVendors(names);
    }).catch(() => setVendors([]));
  }, [open, showVendor]);

  async function download() {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (vendor) params.vendor_name = vendor;
      const r = await api.get(endpoint, { params, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : filename;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Excel downloaded");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setFromDate(""); setToDate(""); setVendor("");
  }

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`${testid}-open-btn`}
        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-emerald-600 text-emerald-700 px-3 py-1.5 hover:bg-emerald-50 bg-white">
        <Download size={13}/> {label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={() => !loading && setOpen(false)}>
          <div className="bg-white border border-slate-200 shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid={`${testid}-dialog`}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="font-display text-base font-bold flex items-center gap-2"><Filter size={16} className="text-[#F97316]"/> Excel Export Filters</h3>
              <button onClick={() => setOpen(false)} disabled={loading} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">From Date</div>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid={`${testid}-from-date`}
                    className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">To Date</div>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid={`${testid}-to-date`}
                    className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
                </div>
              </div>
              {showVendor && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Vendor</div>
                  <select value={vendor} onChange={e => setVendor(e.target.value)} data-testid={`${testid}-vendor`}
                    className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white">
                    <option value="">— All vendors —</option>
                    {vendors.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              )}
              <div className="bg-indigo-50 border border-indigo-200 px-3 py-2 text-[11px] text-indigo-900 leading-relaxed">
                <strong>Note:</strong> The export returns up to <strong>{MAX_ROWS.toLocaleString()}</strong> rows per request. Refine filters if more records are needed. Leave all fields blank to download the most recent {MAX_ROWS.toLocaleString()} records.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
              <button onClick={reset} disabled={loading} data-testid={`${testid}-reset`}
                className="text-[11px] uppercase font-bold tracking-wider text-slate-500 hover:text-slate-800">Reset</button>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)} disabled={loading}
                  className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</button>
                <button onClick={download} disabled={loading} data-testid={`${testid}-download`}
                  className="inline-flex items-center gap-1.5 bg-[#F97316] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
                  {loading ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
                  {loading ? "Generating…" : "Download"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ExportExcelButton;
